import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Injector,
  Input,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import htmlToPdfmake from 'html-to-pdfmake';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { PDFDocument } from 'pdf-lib';
import { Subject, take, takeUntil } from 'rxjs';
import { ChunkPipe } from '../../../../../core/pipes/chunk.pipe';
import { UserDatePipe } from '../../../../../shared/pipes/user-date.pipe';
import { CommonService } from '../../../../../shared/services/common.service';
import {
  BehaviorQuestionnaires,
  TemplateField,
  TemplateSection,
} from '../../../../bip-template/bip-template.interface';
import { SECTION_DISPLAY_NAMES } from '../../../../bip-template/bip-template.enums';
import { APP_LOOKUP_IDS } from '../print-bip/behavior-section-and-lookup-mapping.constant';
import { InsightsService } from '../../../insights.service';
import { ScreenerResultsGraphComponent } from '../assessment-fba/screener-results-graph/screener-results-graph.component';
import { BehaviouralAssessmentGraphData } from '../assessment-fba/assesment-fba.interface';
import { ApiResponses } from '../../../../../core/models/common.models';
import { caseBehaviorCustomQuestionAnswer } from '../../../insights.interface';
import {
  CaseAttachment,
  SubQuestionnaireSection,
} from '../print-options/print-bip.interface';
import {
  BehaviorStrategyDetailPromptAnswerItem,
  StrategyItem,
} from '../manage-behavior-strategies/strategy.interface';
import {
  PrintBipReportBehaviorData,
  PrintBipReportOptions,
} from './print-bip-report.interface';

type PrintSectionKind =
  | 'behavior-definition'
  | 'strategies'
  | 'strategy-details'
  | 'safety-plan'
  | 'behavior-screener'
  | 'reports'
  | 'approvals'
  | 'questionnaire'
  | 'custom-questions-bucket'
  | 'custom-question';

interface PrintSectionMeta {
  section: TemplateSection;
  kind: PrintSectionKind;
  color: string;
}

const SECTION_COLORS = [
  '#17b26a',
  '#175cd3',
  '#6941c6',
  '#dc6803',
  '#0e9384',
  '#d92d20',
];

// Deliberately outside SECTION_COLORS so the behavior banner never matches a section color.
const BEHAVIOR_HEADER_COLOR = '#101828';

// Light "chip" tints so consecutive cards (strategies, plans, etc.) are easy to tell apart at a glance.
const CARD_TITLE_COLORS = [
  { fillColor: '#ECFDF3', color: '#027A48' },
  { fillColor: '#EFF8FF', color: '#175CD3' },
  { fillColor: '#F9F5FF', color: '#6941C6' },
  { fillColor: '#FFFAEB', color: '#B54708' },
  { fillColor: '#FDF2FA', color: '#C11574' },
];

@Component({
  selector: 'app-print-bip-report',
  standalone: true,
  imports: [CommonModule, ChunkPipe, UserDatePipe],
  templateUrl: './print-bip-report.component.html',
})
export class PrintBipReportComponent implements OnDestroy {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  printData: PrintBipReportOptions | null = null;
  @Input() set printOptions(val: PrintBipReportOptions) {
    this.printData = val;
    this.cd.detectChanges();
    this.initialized = true;
  }
  @ViewChild('pdfContent', { static: true }) pdfContent!: ElementRef;
  initialized = false;
  lookUp_Ids = APP_LOOKUP_IDS;
  readonly signatures = new Array(6);
  readonly pageBreakBefore = '{"pageBreak":"before"}';
  readonly behaviorHeaderColor = BEHAVIOR_HEADER_COLOR;

  private readonly destroy$ = new Subject<void>();
  private readonly strategyDetailPromptLookupCache = new WeakMap<
    StrategyItem,
    Map<number, string>
  >();

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly injector: Injector,
    private readonly commonService: CommonService,
    private readonly insightsService: InsightsService
  ) {
    (pdfMake.vfs as unknown as Record<string, string>) =
      pdfFonts as unknown as Record<string, string>;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ----- Ordered, template-driven sections -----

  getOrderedSections(): PrintSectionMeta[] {
    const planNames = new Set(
      [
        ...(this.printData?.caseData?.subQuestionnaireSections || []),
        ...(this.printData?.attachments || []),
      ]
        .map(section => (section.planTypeName || '').trim().toLowerCase())
        .filter(name => !!name)
    );

    const sections = (this.printData?.bipTemplate?.templateSections || [])
      .filter(section => section.isSelected)
      .filter(section => !section.isSubQuestionnaireSection)
      .filter(
        section =>
          !planNames.has((section.sectionName || '').trim().toLowerCase())
      )
      .sort((a, b) => a.sequenceNumber - b.sequenceNumber);

    return sections
      .map((section, index) => ({
        section,
        kind: this.getSectionKind(section),
        color: SECTION_COLORS[index % SECTION_COLORS.length],
      }))
      .filter(meta => meta.kind !== 'questionnaire');
  }

  // Behaviors are the only numbered sections ahead of Approvals/Questionnaires,
  // so their count anchors the sequence for both trailing top-level sections.
  getBehaviorsCount(): number {
    return this.printData?.caseData?.caseBehaviors?.length || 0;
  }

  getApprovalsSectionNumber(): number {
    return this.getBehaviorsCount() + 1;
  }

  getQuestionnairesSectionNumber(): number {
    return this.getBehaviorsCount() + 2;
  }

  getSectionHeaderAttr(color: string): string {
    return JSON.stringify({
      fillColor: color,
      color: '#ffffff',
      margin: [0, 7, 0, 7],
    });
  }

  getPlanTitleAttr(): string {
    return JSON.stringify({
      fillColor: '#EFF8FF',
      color: '#175CD3',
      bold: true,
      margin: [8, 7, 8, 7],
    });
  }

  getStrategyTitleAttr(): string {
    const palette = CARD_TITLE_COLORS[0];
    return JSON.stringify({
      fillColor: palette.fillColor,
      color: palette.color,
      bold: true,
      margin: [8, 7, 8, 7],
    });
  }

  getBehaviorFunctionNames(behavior: PrintBipReportBehaviorData): string[] {
    return (behavior.behaviorFunction || []).map(fn => fn.behaviorFunctionName);
  }

  getAttachmentPlanGroups(): SubQuestionnaireSection[] {
    return (this.printData?.attachments || []).filter(
      section =>
        (section.attachmentCount || section.attachments?.length || 0) > 0
    );
  }

  getQuestionAnswer(
    behavior: PrintBipReportBehaviorData,
    questionId: number
  ): caseBehaviorCustomQuestionAnswer | undefined {
    return behavior?.caseBehaviorCustomQuestionAnswer?.find(
      answer => answer.customQuestionId === questionId
    );
  }

  getOrderedBehaviorQuestionnaires(): BehaviorQuestionnaires[] {
    return (this.printData?.bipTemplate?.behaviorQuestionaries || [])
      .filter(item => item.isSelected)
      .sort(
        (a, b) =>
          a.behaviorTypeSequenceNumber - b.behaviorTypeSequenceNumber ||
          (a.questionSequenceNumber ?? 0) - (b.questionSequenceNumber ?? 0)
      );
  }

  // Custom questions dragged into Challenging Behavior print there (see
  // behaviorDefinitionTpl) and must not also print in the Custom Questions
  // bucket or as their own standalone section.
  private getChallengingBehaviorCustomQuestionIds(): Set<number> {
    return new Set(
      this.getOrderedBehaviorQuestionnaires()
        .map(item => item.questionId)
        .filter((questionId): questionId is number => !!questionId)
    );
  }

  getCustomQuestionsBucket(
    behavior: PrintBipReportBehaviorData
  ): caseBehaviorCustomQuestionAnswer[] {
    const excludedIds = this.getChallengingBehaviorCustomQuestionIds();
    return (behavior.caseBehaviorCustomQuestionAnswer || []).filter(
      answer => !excludedIds.has(answer.customQuestionId)
    );
  }

  private getSectionKind(section: TemplateSection): PrintSectionKind {
    const name = (section.sectionName || '').trim().toLowerCase();
    if (name.includes('questionnaire')) return 'questionnaire';
    if (name.includes('strategy details')) return 'strategy-details';
    if (name.includes('strategie')) return 'strategies';
    if (name.includes('safety plan')) return 'safety-plan';
    if (name.includes('approval')) return 'approvals';
    if (name.includes('screener') || name.includes('function')) {
      return 'behavior-screener';
    }
    if (
      name.includes('graph') ||
      name.includes('report') ||
      name.includes('data')
    ) {
      return 'reports';
    }
    if (
      name.includes('behavior definition') ||
      name.includes('challenging behavior')
    ) {
      return 'behavior-definition';
    }
    if (name === 'custom questions') return 'custom-questions-bucket';
    return 'custom-question';
  }

  getSectionDisplayName(sectionName: string): string {
    return SECTION_DISPLAY_NAMES[sectionName] ?? sectionName;
  }

  getVisibleSections(behavior: PrintBipReportBehaviorData): PrintSectionMeta[] {
    return this.getOrderedSections().filter(meta =>
      this.isSectionVisible(meta, behavior)
    );
  }

  isSectionVisible(
    meta: PrintSectionMeta,
    behavior: PrintBipReportBehaviorData
  ): boolean {
    switch (meta.kind) {
      case 'behavior-definition':
        return true;
      case 'approvals':
        // Approvals prints once at the very end of the document, not per behavior.
        return false;
      case 'strategies':
      case 'strategy-details':
        return (behavior.caseBehaviorStrategies?.length ?? 0) > 0;
      case 'safety-plan':
        return !!this.printData?.caseData?.isEnabledSafetyPlan;
      case 'behavior-screener':
        return this.getBehaviorFunctionNames(behavior).length > 0;
      case 'reports':
        return this.hasReportsData(behavior);
      case 'custom-questions-bucket':
        return this.getCustomQuestionsBucket(behavior).length > 0;
      case 'custom-question': {
        const answer = this.getCustomQuestionAnswer(meta.section, behavior);
        return !!answer?.answer;
      }
      default:
        return false;
    }
  }

  hasReportsData(behavior: PrintBipReportBehaviorData): boolean {
    const groups = behavior.groupedGraphsAndComments || [];
    return groups.some(
      group =>
        (group.graphs?.length ?? 0) > 0 || (group.commentsData?.length ?? 0) > 0
    );
  }

  getCustomQuestionAnswer(
    section: TemplateSection,
    behavior: PrintBipReportBehaviorData
  ): caseBehaviorCustomQuestionAnswer | undefined {
    const target = (section.sectionName || '')
      .replace(/\(custom question\)/i, '')
      .trim()
      .toLowerCase();
    if (!target) return undefined;

    const excludedIds = this.getChallengingBehaviorCustomQuestionIds();
    return behavior.caseBehaviorCustomQuestionAnswer?.find(answer => {
      if (excludedIds.has(answer.customQuestionId)) return false;
      const question = (answer.customQuestion || '').trim().toLowerCase();
      return (
        question === target ||
        question.includes(target) ||
        target.includes(question)
      );
    });
  }

  // ----- Plan Information cover fields -----

  getSelectedFields(): TemplateField[] {
    return (
      this.printData?.bipTemplate?.templateFields.filter(
        field => field.isSelected && field.fieldName !== 'Student Age'
      ) || []
    );
  }

  getFieldValue(fieldName: string): string {
    const caseData = this.printData?.caseData;
    switch (fieldName) {
      case 'Student Date of Birth':
        return caseData?.student?.dateOfBirth
          ? this.formatDate(caseData.student.dateOfBirth)
          : '-';
      case 'Student School':
        return caseData?.student?.organizationName || '-';
      case 'Safety Plan':
        return caseData?.isEnabledSafetyPlan ? 'Yes' : 'No';
      case 'Behavior Screener':
        return caseData?.behaviorScreenerDate
          ? this.formatDate(caseData.behaviorScreenerDate)
          : '-';
      case 'Plan Created':
        return caseData?.planCreatedDate
          ? this.formatDate(caseData.planCreatedDate)
          : '-';
      case 'Last Revised':
        return caseData?.lastRevisedDate
          ? this.formatDate(caseData.lastRevisedDate)
          : '-';
      default:
        return '-';
    }
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('en-US');
  }

  getPlanInformationFields(): { label: string; value: string }[] {
    const selectedFieldNames = new Set(
      this.getSelectedFields().map(field => field.fieldName)
    );
    const candidates: {
      label: string;
      fieldName?: string;
      value: () => string;
    }[] = [
      {
        label: 'School',
        fieldName: 'Student School',
        value: () => this.getFieldValue('Student School'),
      },
      {
        label: 'Plan Created',
        fieldName: 'Plan Created',
        value: () => this.getFieldValue('Plan Created'),
      },
      {
        label: 'Student ID',
        value: () => this.printData?.caseData?.student?.enrollId || '-',
      },
      {
        label: 'Last Revised',
        fieldName: 'Last Revised',
        value: () => this.getFieldValue('Last Revised'),
      },
      {
        label: 'Date of Birth',
        fieldName: 'Student Date of Birth',
        value: () => this.getFieldValue('Student Date of Birth'),
      },
      {
        label: 'Behavior Screener',
        fieldName: 'Behavior Screener',
        value: () => this.getFieldValue('Behavior Screener'),
      },
      {
        label: 'Supervisor',
        value: () => this.printData?.caseData?.supervisorName || '-',
      },
      {
        label: 'Plan Includes Strategy Guidelines',
        value: () =>
          this.printData?.bipTemplate?.isIncludeStrategyGuidelines
            ? 'Yes'
            : 'No',
      },
    ];

    return candidates
      .filter(
        candidate =>
          !candidate.fieldName || selectedFieldNames.has(candidate.fieldName)
      )
      .map(candidate => ({ label: candidate.label, value: candidate.value() }));
  }

  // pdfmake cannot wrap a single unbroken run of characters (e.g. free text
  // typed/pasted without spaces), so long tokens overflow the page width.
  // Zero-width spaces give it break points without changing the visible text.
  wrapLongText(value: string | null | undefined): string {
    if (!value) {
      return value || '';
    }
    const zeroWidthSpace = String.fromCharCode(8203);
    const maxUnbrokenLength = 15;
    return value.replace(/\S+/g, token =>
      token.length > maxUnbrokenLength
        ? (
            token.match(new RegExp(`.{1,${maxUnbrokenLength}}`, 'g')) || []
          ).join(zeroWidthSpace)
        : token
    );
  }

  getFrequencyAnswer(behavior: PrintBipReportBehaviorData): string {
    let text = '';
    if (behavior.behaviourFrequencyId) {
      text = `It usually occurs at least once and possibly several times ${behavior.behaviourFrequencyName}.`;
    }
    if (behavior.behaviourFrequencyCount) {
      text = `${text} On average ${behavior.behaviourFrequencyCount} time(s)`;
    }
    text =
      !!behavior.behaviourFrequencyCount && !!behavior.duration
        ? `${text} for ${behavior.duration} minute(s).`
        : `${text}.`;
    return text;
  }

  getStrategyDetailPromptColumns(strategies: StrategyItem[]): string[] {
    const promptsBySequence = new Map<number, string>();
    for (const strategy of strategies || []) {
      for (const prompt of strategy.behaviorStrategyDetailPrompts || []) {
        const label = (prompt.detailPrompt || '').trim();
        if (label && !promptsBySequence.has(prompt.sequenceNumber)) {
          promptsBySequence.set(prompt.sequenceNumber, label);
        }
      }
    }
    return Array.from(promptsBySequence.keys())
      .sort((a, b) => a - b)
      .map(sequence => promptsBySequence.get(sequence) as string);
  }

  getStrategyDetailAnswerForColumn(
    strategy: StrategyItem,
    columnLabel: string
  ): string {
    const target = columnLabel.trim().toLowerCase();
    const answer = (strategy.detailPromptAnswers || []).find(
      candidate =>
        this.getStrategyDetailPromptQuestion(strategy, candidate)
          .trim()
          .toLowerCase() === target
    );
    return answer?.answer || '-';
  }

  getStrategyDetailPromptQuestion(
    strategy: StrategyItem,
    detailPromptAnswer: BehaviorStrategyDetailPromptAnswerItem
  ): string {
    let promptLookup = this.strategyDetailPromptLookupCache.get(strategy);
    if (!promptLookup) {
      promptLookup = new Map<number, string>();
      strategy.behaviorStrategyDetailPrompts?.forEach(prompt => {
        promptLookup?.set(prompt.id, prompt.detailPrompt || '');
      });
      this.strategyDetailPromptLookupCache.set(strategy, promptLookup);
    }
    return (
      promptLookup.get(detailPromptAnswer.behaviorStrategyDetailPromptId) ||
      detailPromptAnswer.detailPrompt ||
      ''
    );
  }

  // ----- Behavior Function / Screener graph generation (new section, not in old report) -----

  private generateFunctionGraphImages(): Promise<void[]> {
    const behaviors = (this.printData?.caseData?.caseBehaviors || []).filter(
      behavior => this.getBehaviorFunctionNames(behavior).length > 0
    );
    return Promise.all(
      behaviors.map(behavior => this.generateFunctionGraphForBehavior(behavior))
    );
  }

  private generateFunctionGraphForBehavior(
    behavior: PrintBipReportBehaviorData
  ): Promise<void> {
    return new Promise<void>(resolve => {
      this.insightsService
        .getBehvaiourGraphData(behavior.id)
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe({
          next: (res: ApiResponses<BehaviouralAssessmentGraphData>) => {
            const graphData = res?.result || [];
            if (!graphData.length) {
              resolve();
              return;
            }
            this.renderFunctionGraphImage(behavior, graphData, resolve);
          },
          error: () => resolve(),
        });
    });
  }

  private renderFunctionGraphImage(
    behavior: PrintBipReportBehaviorData,
    graphData: BehaviouralAssessmentGraphData[],
    resolve: () => void
  ): void {
    const factory = this.viewContainerRef.createComponent(
      ScreenerResultsGraphComponent,
      { injector: this.injector }
    );
    const componentElement = factory.location.nativeElement;
    componentElement.style.position = 'fixed';
    componentElement.style.left = '-10000px';
    componentElement.style.top = '0';
    componentElement.style.visibility = 'hidden';
    componentElement.style.pointerEvents = 'none';

    let isSettled = false;
    const timeoutId = globalThis.setTimeout(() => {
      if (isSettled) return;
      isSettled = true;
      factory.destroy();
      resolve();
    }, 7000);

    factory.instance.chartImage
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (image: string) => {
          if (isSettled) return;
          isSettled = true;
          globalThis.clearTimeout(timeoutId);
          behavior.behaviorFunctionGraphImage = image;
          factory.destroy();
          resolve();
        },
        error: () => {
          if (isSettled) return;
          isSettled = true;
          globalThis.clearTimeout(timeoutId);
          factory.destroy();
          resolve();
        },
      });

    factory.instance.chartId = `print-bip-function-graph-${behavior.id}`;
    factory.instance.graphData = graphData;
    factory.changeDetectorRef.detectChanges();
  }

  // ----- PDF generation -----

  async printPDF(): Promise<void> {
    if (!this.printData?.caseData) {
      return;
    }

    await this.generateFunctionGraphImages();
    this.cd.detectChanges();

    const pdfContent = document.getElementById('pdf-report-data');
    if (!pdfContent) {
      return;
    }

    const pdfMakeContent = htmlToPdfmake(pdfContent.innerHTML);
    const pdfData = this.printData;

    const logoBase64 = pdfData?.organizationDetail?.logoUrl
      ? await this.commonService.getBase64ImageFromUrl(
          pdfData.organizationDetail.logoUrl
        )
      : await this.commonService.getBase64ImageFromUrl(
          '/assets/images/logo.png'
        );

    const columns: any[] = [
      logoBase64
        ? {
            image: logoBase64,
            width: 125,
            fit: [125, 40],
            alignment: 'left',
          }
        : {},
      {
        stack: [
          `${pdfData?.organizationDetail?.address || ''}, ${
            pdfData?.organizationDetail?.city || ''
          }`,
          `${pdfData?.organizationDetail?.state || ''}, ${
            pdfData?.organizationDetail?.country || ''
          }, ${pdfData?.organizationDetail?.zipCode || ''}`,
        ],
        fontSize: 10,
        alignment: 'right',
      },
    ];

    const waterMarkOption = {
      text: 'DRAFT',
      color: 'gray',
      opacity: 0.2,
    };

    const documentDefinition: TDocumentDefinitions = {
      content: pdfMakeContent,
      pageMargins: [20, 60, 20, 40],
      header: (currentPage, pageCount, pageSize) => {
        const pageWidth =
          typeof pageSize === 'object' ? pageSize.width : 595.28;
        const lineWidth = 552;
        return [
          {
            margin: [20, 10, 20, 0],
            columns: columns as any,
          },
          {
            canvas: [
              {
                type: 'line',
                x1: (pageWidth - lineWidth) / 2,
                y1: 0,
                x2: (pageWidth - lineWidth) / 2 + lineWidth,
                y2: 0,
                lineWidth: 1,
                lineColor: '#eaecf0',
              },
            ],
            margin: [0, 5, 0, 8],
          },
        ];
      },
      footer: function (currentPage: number) {
        return {
          columns: [
            { text: '', alignment: 'left' },
            {
              text: `Powered by Insights to Behavior®`,
              alignment: 'center',
            },
            { text: `${currentPage}`, alignment: 'right' },
          ],
          margin: [10, 20, 10, 0],
          color: '#475467',
        };
      },
      watermark: pdfData?.PrintOptions?.isDraft
        ? waterMarkOption
        : ({} as { text: string; color: string; opacity: number }),
      styles: {
        desc: { fontSize: 12 },
        address: { fontSize: 12 },
        title: { fontSize: 12 },
        datalabel: { lineHeight: 1.5, color: '#475467' },
        extraLineHeight: { lineHeight: 1.5 },
        dataValue: { color: '#475467' },
        columns: { columnGap: 10 },
      },
      defaultStyle: {
        columnGap: 10,
        fontSize: 12,
      },
    };

    const pdfBuffer = await new Promise<Uint8Array>(resolve => {
      pdfMake.createPdf(documentDefinition).getBuffer((buffer: Uint8Array) => {
        resolve(buffer);
      });
    });

    const planGroups = this.getAttachmentPlanGroups();

    const mergedPdfBytes = await this.mergeAttachments(pdfBuffer, planGroups);
    this.downloadMergedPdf(mergedPdfBytes);

    this.initialized = false;
    this.ngOnDestroy();
  }

  private isPdfAttachment(attachment: CaseAttachment): boolean {
    const PDF_MEDIA_TYPE = 1;
    return (
      attachment.mediaTypeId === PDF_MEDIA_TYPE ||
      (attachment.name || '').toLowerCase().endsWith('.pdf')
    );
  }

  private async mergeAttachments(
    baseBytes: Uint8Array,
    planGroups: SubQuestionnaireSection[]
  ): Promise<Uint8Array> {
    const mergedPdfDoc = await PDFDocument.load(baseBytes);

    for (const planGroup of planGroups) {
      for (const attachment of planGroup.attachments || []) {
        if (!attachment.fileUrl) continue;
        try {
          const response = await fetch(attachment.fileUrl);
          const bytes = new Uint8Array(await response.arrayBuffer());

          if (this.isPdfAttachment(attachment)) {
            const pdfDoc = await PDFDocument.load(bytes);
            const copiedPages = await mergedPdfDoc.copyPages(
              pdfDoc,
              pdfDoc.getPageIndices()
            );
            copiedPages.forEach(page => mergedPdfDoc.addPage(page));
          } else {
            await this.appendImageAttachment(
              mergedPdfDoc,
              bytes,
              attachment.name
            );
          }
        } catch (error) {
          console.error('Failed to merge attachment into print PDF', error);
        }
      }
    }

    return mergedPdfDoc.save();
  }

  private async appendImageAttachment(
    doc: PDFDocument,
    bytes: Uint8Array,
    name: string
  ): Promise<void> {
    const isPng = (name || '').toLowerCase().endsWith('.png');
    const image = isPng ? await doc.embedPng(bytes) : await doc.embedJpg(bytes);
    const page = doc.addPage([image.width, image.height]);
    page.drawImage(image, {
      x: 0,
      y: 0,
      width: image.width,
      height: image.height,
    });
  }

  private downloadMergedPdf(pdfBytes: Uint8Array): void {
    const blob = new Blob([pdfBytes.buffer as ArrayBuffer], {
      type: 'application/pdf',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Plan_${this.printData?.caseData?.student?.firstName}_${this.printData?.caseData?.student?.lastName}.pdf`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
